import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDoc,
  getDocs,
  doc,
  query,
  where,
} from "firebase/firestore";

import db from "../../Firebase";
import ConsoleHomePageHeader from "../Home/ConsoleHomePageHeader";

import "./ConversationViewer.css";

const PAGE_SIZE = 100;

const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === "string" || typeof value === "number") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const seconds = value.seconds ?? value._seconds;
  if (typeof seconds === "number") return new Date(seconds * 1000);
  return null;
};

const activityDate = (conversation) =>
  toDate(conversation.lastMessageAt) ||
  toDate(conversation.updatedAt) ||
  toDate(conversation.createdAt);

const formatDateTime = (value) => {
  const date = toDate(value);
  if (!date) return "Unknown";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const userName = (user, id) =>
  user?.name || user?.displayName || user?.email || id || "Unknown user";

const plantName = (plant, id) =>
  plant?.name || plant?.commonName || plant?.species || id || "Unknown plant";

const fetchReferencedDocuments = async (collectionName, ids) => {
  const results = {};
  const uniqueIds = [...new Set(ids.filter(Boolean))];

  // Keep the number of simultaneous reads modest for large conversation sets.
  for (let offset = 0; offset < uniqueIds.length; offset += 20) {
    const batch = uniqueIds.slice(offset, offset + 20);
    // eslint-disable-next-line no-await-in-loop
    const snapshots = await Promise.all(
      batch.map((id) => getDoc(doc(db, collectionName, id)).catch(() => null))
    );
    snapshots.forEach((snapshot, index) => {
      if (snapshot?.exists()) results[batch[index]] = snapshot.data();
    });
  }

  return results;
};

const ConversationViewer = () => {
  const [conversations, setConversations] = useState([]);
  const [usersById, setUsersById] = useState({});
  const [plantsById, setPlantsById] = useState({});
  const [selectedId, setSelectedId] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [error, setError] = useState("");
  const [messageError, setMessageError] = useState("");
  const [search, setSearch] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [plantFilter, setPlantFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDateFilter, setToDateFilter] = useState("");
  const [sort, setSort] = useState("newest");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const loadConversations = async () => {
    setLoading(true);
    setError("");
    try {
      const snapshot = await getDocs(collection(db, "PlantConversations"));
      const loaded = snapshot.docs.map((item) => ({
        ...item.data(),
        id: item.id,
      }));
      setConversations(loaded);

      const [users, plants] = await Promise.all([
        fetchReferencedDocuments(
          "Users",
          loaded.map((conversation) => conversation.userId)
        ),
        fetchReferencedDocuments(
          "Plants",
          loaded.map((conversation) => conversation.plantId)
        ),
      ]);
      setUsersById(users);
      setPlantsById(plants);
    } catch (loadError) {
      setError(`Could not load conversations: ${loadError.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConversations();
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setMessages([]);
      return undefined;
    }

    let cancelled = false;
    const loadMessages = async () => {
      setMessagesLoading(true);
      setMessageError("");
      setMessages([]);
      try {
        const snapshot = await getDocs(
          query(
            collection(db, "PlantMessages"),
            where("conversationId", "==", selectedId)
          )
        );
        const loaded = snapshot.docs
          .map((item) => ({ ...item.data(), id: item.id }))
          .sort((a, b) => {
            const aTime = toDate(a.createdAt)?.getTime() || 0;
            const bTime = toDate(b.createdAt)?.getTime() || 0;
            if (aTime !== bTime) return aTime - bTime;
            return a.id.localeCompare(b.id);
          });
        if (!cancelled) setMessages(loaded);
      } catch (loadError) {
        if (!cancelled) {
          setMessageError(`Could not load messages: ${loadError.message}`);
        }
      } finally {
        if (!cancelled) setMessagesLoading(false);
      }
    };

    loadMessages();
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [search, userFilter, plantFilter, fromDate, toDateFilter, sort]);

  const userOptions = useMemo(() => {
    const ids = [...new Set(conversations.map((item) => item.userId).filter(Boolean))];
    return ids.sort((a, b) =>
      userName(usersById[a], a).localeCompare(userName(usersById[b], b))
    );
  }, [conversations, usersById]);

  const plantOptions = useMemo(() => {
    const ids = [...new Set(conversations.map((item) => item.plantId).filter(Boolean))];
    return ids.sort((a, b) =>
      plantName(plantsById[a], a).localeCompare(plantName(plantsById[b], b))
    );
  }, [conversations, plantsById]);

  const filteredConversations = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    const from = fromDate ? new Date(`${fromDate}T00:00:00`) : null;
    const through = toDateFilter ? new Date(`${toDateFilter}T23:59:59.999`) : null;

    const filtered = conversations.filter((conversation) => {
      if (userFilter && conversation.userId !== userFilter) return false;
      if (plantFilter && conversation.plantId !== plantFilter) return false;

      const date = activityDate(conversation);
      if (from && (!date || date < from)) return false;
      if (through && (!date || date > through)) return false;

      if (!normalizedSearch) return true;
      const user = usersById[conversation.userId];
      const plant = plantsById[conversation.plantId];
      return [
        conversation.id,
        conversation.userId,
        conversation.plantId,
        conversation.lastMessagePreview,
        user?.name,
        user?.displayName,
        user?.email,
        plant?.name,
        plant?.species,
      ].some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(normalizedSearch)
      );
    });

    return filtered.sort((a, b) => {
      if (sort === "user") {
        return userName(usersById[a.userId], a.userId).localeCompare(
          userName(usersById[b.userId], b.userId)
        );
      }
      if (sort === "plant") {
        return plantName(plantsById[a.plantId], a.plantId).localeCompare(
          plantName(plantsById[b.plantId], b.plantId)
        );
      }
      const aTime = activityDate(a)?.getTime() || 0;
      const bTime = activityDate(b)?.getTime() || 0;
      return sort === "oldest" ? aTime - bTime : bTime - aTime;
    });
  }, [
    conversations,
    fromDate,
    plantFilter,
    plantsById,
    search,
    sort,
    toDateFilter,
    userFilter,
    usersById,
  ]);

  const selectedConversation = conversations.find(
    (conversation) => conversation.id === selectedId
  );
  const selectedUser = selectedConversation
    ? usersById[selectedConversation.userId]
    : null;
  const selectedPlant = selectedConversation
    ? plantsById[selectedConversation.plantId]
    : null;
  const uniqueUsers = new Set(conversations.map((item) => item.userId).filter(Boolean));
  const uniquePlants = new Set(conversations.map((item) => item.plantId).filter(Boolean));

  const clearFilters = () => {
    setSearch("");
    setUserFilter("");
    setPlantFilter("");
    setFromDate("");
    setToDateFilter("");
    setSort("newest");
  };

  return (
    <div className="conversation-viewer-page">
      <ConsoleHomePageHeader />
      <main className="conversation-viewer">
        <div className="conversation-viewer__heading">
          <div>
            <p className="conversation-viewer__eyebrow">Admin console</p>
            <h1>Plant conversations</h1>
            <p>Review conversations across users and Leaflings.</p>
          </div>
          <button
            type="button"
            className="conversation-viewer__refresh"
            onClick={loadConversations}
            disabled={loading}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        <section className="conversation-viewer__metrics" aria-label="Conversation totals">
          <div><strong>{conversations.length}</strong><span>Conversations</span></div>
          <div><strong>{uniqueUsers.size}</strong><span>Users</span></div>
          <div><strong>{uniquePlants.size}</strong><span>Plants</span></div>
          <div><strong>{filteredConversations.length}</strong><span>Matching</span></div>
        </section>

        <section className="conversation-filters" aria-label="Conversation filters">
          <label className="conversation-filters__search">
            <span>Search</span>
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Name, email, plant, preview, or ID"
            />
          </label>
          <label>
            <span>User</span>
            <select value={userFilter} onChange={(event) => setUserFilter(event.target.value)}>
              <option value="">All users</option>
              {userOptions.map((id) => (
                <option key={id} value={id}>{userName(usersById[id], id)}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Plant</span>
            <select value={plantFilter} onChange={(event) => setPlantFilter(event.target.value)}>
              <option value="">All plants</option>
              {plantOptions.map((id) => (
                <option key={id} value={id}>{plantName(plantsById[id], id)}</option>
              ))}
            </select>
          </label>
          <label>
            <span>From</span>
            <input type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} />
          </label>
          <label>
            <span>Through</span>
            <input type="date" value={toDateFilter} onChange={(event) => setToDateFilter(event.target.value)} />
          </label>
          <label>
            <span>Sort</span>
            <select value={sort} onChange={(event) => setSort(event.target.value)}>
              <option value="newest">Newest activity</option>
              <option value="oldest">Oldest activity</option>
              <option value="user">User A–Z</option>
              <option value="plant">Plant A–Z</option>
            </select>
          </label>
          <button type="button" className="conversation-filters__clear" onClick={clearFilters}>
            Clear filters
          </button>
        </section>

        {error ? <div className="conversation-viewer__error" role="alert">{error}</div> : null}

        <div className="conversation-workspace">
          <section className="conversation-list" aria-label="Conversations">
            <div className="conversation-list__header">
              <span>Conversation</span>
              <span>{filteredConversations.length} results</span>
            </div>

            {loading && conversations.length === 0 ? (
              <div className="conversation-list__state">Loading conversations…</div>
            ) : filteredConversations.length === 0 ? (
              <div className="conversation-list__state">No conversations match these filters.</div>
            ) : (
              filteredConversations.slice(0, visibleCount).map((conversation) => {
                const user = usersById[conversation.userId];
                const plant = plantsById[conversation.plantId];
                return (
                  <button
                    type="button"
                    key={conversation.id}
                    className={`conversation-row${selectedId === conversation.id ? " conversation-row--selected" : ""}`}
                    onClick={() => setSelectedId(conversation.id)}
                  >
                    <span className="conversation-row__identity">
                      <span className="conversation-row__plant">{plantName(plant, conversation.plantId)}</span>
                      <span className="conversation-row__user">{userName(user, conversation.userId)}</span>
                    </span>
                    <span className="conversation-row__preview">
                      {conversation.lastMessagePreview || "No message preview"}
                    </span>
                    <span className="conversation-row__time">{formatDateTime(activityDate(conversation))}</span>
                  </button>
                );
              })
            )}

            {visibleCount < filteredConversations.length ? (
              <button
                type="button"
                className="conversation-list__more"
                onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
              >
                Show {Math.min(PAGE_SIZE, filteredConversations.length - visibleCount)} more
              </button>
            ) : null}
          </section>

          <aside className="conversation-detail" aria-label="Selected conversation">
            {!selectedConversation ? (
              <div className="conversation-detail__empty">
                <span aria-hidden="true">🌱</span>
                <h2>Select a conversation</h2>
                <p>Choose a row to read the full exchange.</p>
              </div>
            ) : (
              <>
                <div className="conversation-detail__header">
                  <div>
                    <p>{userName(selectedUser, selectedConversation.userId)} with</p>
                    <h2>{plantName(selectedPlant, selectedConversation.plantId)}</h2>
                    {selectedPlant?.species ? <span>{selectedPlant.species}</span> : null}
                  </div>
                  <button type="button" onClick={() => setSelectedId("")} aria-label="Close conversation">×</button>
                </div>
                <dl className="conversation-detail__metadata">
                  <div><dt>Started</dt><dd>{formatDateTime(selectedConversation.createdAt)}</dd></div>
                  <div><dt>Last activity</dt><dd>{formatDateTime(activityDate(selectedConversation))}</dd></div>
                  <div><dt>User ID</dt><dd title={selectedConversation.userId}>{selectedConversation.userId}</dd></div>
                  <div><dt>Plant ID</dt><dd title={selectedConversation.plantId}>{selectedConversation.plantId}</dd></div>
                </dl>

                <div className="conversation-messages">
                  {messagesLoading ? (
                    <div className="conversation-list__state">Loading messages…</div>
                  ) : messageError ? (
                    <div className="conversation-viewer__error" role="alert">{messageError}</div>
                  ) : messages.length === 0 ? (
                    <div className="conversation-list__state">No saved messages in this conversation.</div>
                  ) : (
                    messages.map((message) => (
                      <article
                        key={message.id}
                        className={`conversation-message conversation-message--${message.role === "user" ? "user" : "assistant"}`}
                      >
                        <div>
                          <strong>{message.role === "user" ? userName(selectedUser, "User") : plantName(selectedPlant, "Leafling")}</strong>
                          <time>{formatDateTime(message.createdAt)}</time>
                        </div>
                        <p>{message.content}</p>
                      </article>
                    ))
                  )}
                </div>
              </>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
};

export default ConversationViewer;
